// Conversation service — manages chat conversation lifecycle via REST API

import { apiFetch } from './apiClient';
import type { Conversation, ConversationListResponse, ConversationMessagesResponse, ConversationActiveResponse } from '@/types/api';

export async function listConversations(): Promise<Conversation[]> {
  const res = await apiFetch('/api/conversations');
  if (!res.ok) throw new Error(`Failed to fetch conversations: ${res.status}`);
  return res.json();
}

export async function getMessages(conversationId: string, limit: number = 100): Promise<ConversationMessagesResponse> {
  const res = await apiFetch(`/api/conversations/${conversationId}/messages?limit=${limit}`);
  if (!res.ok) throw new Error(`Failed to fetch messages: ${res.status}`);
  return res.json();
}

export async function closeConversation(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}/close`, { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to close conversation: ${res.status}`);
}

export async function getActiveConversation(): Promise<ConversationActiveResponse | null> {
  const res = await apiFetch('/api/conversations/active');
  if (!res.ok) return null;
  return res.json();
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await apiFetch(`/api/conversations/${conversationId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete conversation: ${res.status}`);
}

export interface ConversationSearchResult {
  id: string;
  conversationId: string;
  role: string;
  message: string;
  timestamp: string;
}

export async function searchConversations(query: string, limit = 200): Promise<ConversationSearchResult[]> {
  const res = await apiFetch(`/api/conversations/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  if (!res.ok) throw new Error(`Failed to search conversations: ${res.status}`);
  const data = await res.json();
  return data.results || [];
}

export async function patchConversation(
  conversationId: string,
  patch: { pinned?: boolean; starred?: boolean; title?: string },
): Promise<Conversation> {
  const res = await apiFetch(`/api/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to patch conversation: ${res.status}`);
  const data = await res.json();
  return data.conversation;
}

export async function createConversation(opts: {
  title?: string;
  projectId?: string;
  agentId?: string;
} = {}): Promise<Conversation> {
  const res = await apiFetch(`/api/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!res.ok) throw new Error(`Failed to create conversation: ${res.status}`);
  const data = await res.json();
  return data.conversation;
}
